/**
 * Custom play/pause control for PDP gallery video.
 *
 * One control, bottom-right of the frame, drawn to match the zoom "+" marker.
 * It drives two very different players behind the same button:
 *
 *   - Shopify-hosted video  -> a plain <video>; we drop its native `controls`
 *     chrome and call play()/pause() directly.
 *   - External video (YouTube) -> an <iframe>; we add `enablejsapi=1` to the
 *     template's src BEFORE <deferred-media> clones it (so the iframe is only
 *     ever loaded once), then drive it through the IFrame Player API.
 *
 * PDP videos play on their own, muted, once they scroll into view: these are
 * fit clips with no sound, and making someone press play first is a barrier to
 * seeing them at all. The control is therefore a pause button by default and
 * only offers play once the video has been paused.
 *
 * <deferred-media> normally injects the real player on first activation, so
 * autoplay means activating it ourselves. That is done from an
 * IntersectionObserver rather than on load, so a video far down the gallery
 * does not start playing off-screen.
 */
(function () {
  // The product section re-renders on variant change and re-runs its scripts.
  // Without this guard a second copy of the gallery video controls binds its own document
  // listeners, and the two cancel each other out on alternate interactions.
  if (window.__weldunVideoControlsLoaded) return;
  window.__weldunVideoControlsLoaded = true;

  var YT_API = 'https://www.youtube.com/iframe_api';
  var ytApiState = null; // null | 'loading' | 'ready'
  var ytApiQueue = [];

  var ICON_PAUSE =
    '<svg width="9" height="15" viewBox="0 0 9 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M0.5 0V15" stroke="currentColor"/><path d="M8 0V15" stroke="currentColor"/></svg>';
  var ICON_PLAY =
    '<svg width="12" height="15" viewBox="0 0 12 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M0.5 0.5L11.5 7.5L0.5 14.5V0.5Z" stroke="currentColor" stroke-linejoin="round"/></svg>';

  /* ---------------------------------------------------------------- YouTube */

  function whenYtReady(callback) {
    if (ytApiState === 'ready') return callback();
    ytApiQueue.push(callback);
    if (ytApiState === 'loading') return;
    ytApiState = 'loading';

    // Chain onto any existing hook rather than clobbering it — Shopify or
    // another app may already be waiting on the same callback.
    var previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      if (typeof previous === 'function') previous();
      ytApiState = 'ready';
      ytApiQueue.splice(0).forEach(function (fn) {
        fn();
      });
    };

    if (window.YT && window.YT.Player) {
      window.onYouTubeIframeAPIReady();
      return;
    }

    var script = document.createElement('script');
    script.src = YT_API;
    script.async = true;
    document.head.appendChild(script);
  }

  // The iframe has to carry enablejsapi=1 from its very first load; adding it
  // afterwards means reassigning src and fetching the video twice.
  function prepareTemplate(deferred) {
    var template = deferred.querySelector('template');
    if (!template || template.dataset.videoControlsPrepared === 'true') return;
    template.dataset.videoControlsPrepared = 'true';

    var iframe = template.content.querySelector('iframe');
    if (iframe) {
      var src = iframe.getAttribute('src') || '';
      if (src) {
        // Each parameter is set on its own rather than behind one "is
        // enablejsapi missing?" check: Shopify already emits enablejsapi=1 and
        // autoplay=1 here, so a combined check skipped everything else —
        // including mute, without which the browser refuses to autoplay at all.
        // Overwriting rather than only appending also fixes an existing
        // autoplay=0 / mute=0.
        var setParam = function (key, value) {
          var pattern = new RegExp('([?&]' + key + '=)[^&]*');
          if (pattern.test(src)) src = src.replace(pattern, '$1' + value);
          else src += (src.indexOf('?') === -1 ? '?' : '&') + key + '=' + value;
        };
        setParam('enablejsapi', '1');
        setParam('autoplay', '1');
        // Autoplay is only permitted while muted. These are silent fit clips,
        // so there is nothing to lose by it.
        setParam('mute', '1');
        // Keeps iOS from taking the video fullscreen the moment it starts.
        setParam('playsinline', '1');
        if (src.indexOf('origin=') === -1) {
          try {
            src += '&origin=' + encodeURIComponent(window.location.origin);
          } catch (e) {
            /* origin is best-effort; the API still works without it */
          }
        }
        iframe.setAttribute('src', src);
      }
    }

    var video = template.content.querySelector('video');
    if (video) {
      // Our control replaces the browser's chrome.
      video.removeAttribute('controls');
      // Muted is a hard requirement for autoplay; playsinline stops iOS taking
      // the video fullscreen the moment it starts.
      video.muted = true;
      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('playsinline', '');
    }
  }

  /* ------------------------------------------------------------- Controller */

  function Controller(deferred) {
    this.deferred = deferred;
    this.playing = false;
    this.player = null; // YT.Player
    this.video = null; // HTMLVideoElement
    this.button = null;
  }

  Controller.prototype.render = function () {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'product__video-control';
    button.addEventListener('click', this.onClick.bind(this));
    this.button = button;
    this.deferred.appendChild(button);
    // Starts as a pause control: the video autoplays. If autoplay is refused
    // (data saver, reduced motion, an old browser) the state sync in attach()
    // flips it back to play.
    this.setPlaying(true);
  };

  Controller.prototype.setPlaying = function (playing) {
    this.playing = playing;
    if (!this.button) return;
    this.button.innerHTML = playing ? ICON_PAUSE : ICON_PLAY;
    this.button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    this.deferred.classList.toggle('is-playing', playing);
  };

  Controller.prototype.onClick = function (event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.deferred.getAttribute('loaded') && !this.deferred.querySelector('video, iframe')) {
      // First press: let <deferred-media> inject the player, then attach.
      this.activate();
      return;
    }
    this.toggle();
  };

  Controller.prototype.activate = function () {
    var poster = this.deferred.querySelector('[id^="Deferred-Poster-"]');
    if (poster) poster.click();
    else if (typeof this.deferred.loadContent === 'function') this.deferred.loadContent(false);
    this.attachWhenReady();
  };

  // Same thing, but for autoplay rather than a press. loadContent is preferred
  // over clicking the poster so activation cannot move focus or scroll the page
  // out from under someone.
  Controller.prototype.autoActivate = function () {
    if (this.activated) return;
    this.activated = true;
    if (typeof this.deferred.loadContent === 'function') this.deferred.loadContent(false);
    else {
      var poster = this.deferred.querySelector('[id^="Deferred-Poster-"]');
      if (poster) poster.click();
    }
    this.attachWhenReady();
  };

  Controller.prototype.attachWhenReady = function () {
    var self = this;
    var tries = 0;
    var poll = setInterval(function () {
      tries += 1;
      if (self.attach() || tries > 40) clearInterval(poll);
    }, 100);
  };

  Controller.prototype.attach = function () {
    if (this.player || this.video) return true;

    var video = this.deferred.querySelector('video');
    if (video) {
      this.video = video;
      video.removeAttribute('controls');
      video.addEventListener('play', this.setPlaying.bind(this, true));
      video.addEventListener('pause', this.setPlaying.bind(this, false));
      video.addEventListener('ended', this.setPlaying.bind(this, false));
      this.setPlaying(!video.paused);
      return true;
    }

    var iframe = this.deferred.querySelector('iframe');
    if (!iframe) return false;

    var self = this;
    whenYtReady(function () {
      if (self.player) return;
      self.player = new window.YT.Player(iframe, {
        events: {
          onReady: function () {
            // Autoplay can be refused (browser policy, data-saver, muted rules),
            // so read the real state rather than assuming it started.
            self.syncFromPlayer();
          },
          onStateChange: function (event) {
            var YTState = window.YT.PlayerState;
            if (event.data === YTState.PLAYING) self.setPlaying(true);
            else if (event.data === YTState.PAUSED || event.data === YTState.ENDED) self.setPlaying(false);
          },
        },
      });

      // onReady does not always arrive (blocked frames, slow API); a short
      // poll keeps the icon honest either way.
      var syncTries = 0;
      var syncPoll = setInterval(function () {
        syncTries += 1;
        if (self.syncFromPlayer() || syncTries > 20) clearInterval(syncPoll);
      }, 250);
    });
    return true;
  };

  // Reads the player's own state. Returns true once the player answers.
  Controller.prototype.syncFromPlayer = function () {
    if (!this.player || typeof this.player.getPlayerState !== 'function') return false;
    var state;
    try {
      state = this.player.getPlayerState();
    } catch (e) {
      return false;
    }
    if (typeof state !== 'number' || state === -1) return false;
    this.setPlaying(state === window.YT.PlayerState.PLAYING);
    return true;
  };

  // Last resort when the API object is unusable (cross-origin quirks): the
  // embed also accepts plain postMessage commands.
  Controller.prototype.postToFrame = function (command) {
    var iframe = this.deferred.querySelector('iframe');
    if (!iframe || !iframe.contentWindow) return false;
    try {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: command, args: [] }),
        '*'
      );
      return true;
    } catch (e) {
      return false;
    }
  };

  Controller.prototype.toggle = function () {
    if (this.video) {
      if (this.video.paused) this.video.play();
      else this.video.pause();
      return;
    }
    if (this.player && typeof this.player.playVideo === 'function') {
      var YTState = window.YT.PlayerState;
      var state = null;
      try {
        state = this.player.getPlayerState();
      } catch (err) {
        state = null;
      }
      var isPlaying = state === YTState.PLAYING;
      try {
        if (isPlaying) this.player.pauseVideo();
        else this.player.playVideo();
        this.setPlaying(!isPlaying);
        return;
      } catch (err) {
        /* fall through to postMessage */
      }
    }

    if (this.deferred.querySelector('iframe')) {
      var next = !this.playing;
      if (this.postToFrame(next ? 'playVideo' : 'pauseVideo')) {
        this.setPlaying(next);
        return;
      }
    }

    // Player not attached yet (activation still in flight) — try again shortly.
    this.attachWhenReady();
  };

  /* ------------------------------------------------------------------ Setup */

  function isVideo(deferred) {
    var template = deferred.querySelector('template');
    if (!template) return false;
    return !!template.content.querySelector('video, iframe');
  }

  function bind(deferred) {
    if (deferred.dataset.videoControls === 'true') return;
    if (!isVideo(deferred)) return;
    deferred.dataset.videoControls = 'true';

    prepareTemplate(deferred);

    var controller = new Controller(deferred);
    controller.render();

    // Play once it is actually on screen, so a video further down the gallery
    // is not running out of sight.
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          controller.autoActivate();
        });
      }, { threshold: 0.4 });
      observer.observe(deferred);
    } else {
      controller.autoActivate();
    }

    // The poster can also be clicked directly; keep the control in sync.
    var poster = deferred.querySelector('[id^="Deferred-Poster-"]');
    if (poster) {
      poster.addEventListener('click', function () {
        controller.attachWhenReady();
      });
    }
  }

  function scan(root) {
    (root || document).querySelectorAll('.product__media-list deferred-media, .product-media-modal deferred-media').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }

  // Variant swaps re-render the gallery.
  new MutationObserver(function (records) {
    records.forEach(function (record) {
      record.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('deferred-media')) bind(node);
        else scan(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
